/**
 * Build CloudFront / image CDN URLs.
 *
 * Public CDN path (filters yok):
 *   /1260x700/85/cover/haber/.../file.webp
 *   /640x360/70/inside/haber/.../file.webp
 *
 * CloudFront Function (`backend/cloudfront/image-path-rewrite.js`)
 * bunu AWS DIT Thumbor path’ine çevirir.
 *
 * Nest /api/media/transform: query string (Sharp).
 */

import { API_BASE_URL } from './config';

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
  format?: 'webp' | 'avif' | 'jpeg' | 'jpg' | 'png';
  quality?: number;
  /** Degrees 0–359 */
  rotate?: number;
  /** Vertical mirror */
  flip?: boolean;
  /** Horizontal mirror */
  flop?: boolean;
  /** Black & white */
  greyscale?: boolean;
  grayscale?: boolean; // alias
}

export type ParamStyle = 'query' | 'short' | 'edits' | 'dit' | 'path';

const ALLOWED_WIDTHS = [
  100, 160, 176, 200, 240, 250, 320, 352, 400, 630, 640, 720, 800, 960, 1200, 1260, 1280, 1920,
];
const ALLOWED_HEIGHTS = [
  100, 112, 120, 133, 192, 200, 224, 250, 350, 360, 384, 400, 420, 450, 533, 540, 675, 700, 720,
  1080,
];

const DEFAULT_FORMAT: 'webp' | 'avif' = 'webp';
const DEFAULT_QUALITY = 100;
const DEFAULT_FIT: ImageTransformOptions['fit'] = 'cover';

function snapToAllowed(value: number, allowed: number[]): number {
  if (allowed.includes(value)) return value;
  let nearest = allowed[0];
  let minDiff = Math.abs(value - nearest);
  for (const allowedValue of allowed) {
    const diff = Math.abs(value - allowedValue);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = allowedValue;
    }
  }
  return nearest;
}

/**
 * Only apply defaults when we are actually resizing.
 * Empty options → original object URL (no Thumbor re-encode).
 */
function normalizeOptions(options: ImageTransformOptions = {}): ImageTransformOptions {
  const greyscale = options.greyscale ?? options.grayscale;
  const hasSize = options.width !== undefined || options.height !== undefined;
  const hasFx =
    options.rotate !== undefined ||
    !!options.flip ||
    !!options.flop ||
    !!greyscale;

  const normalized: ImageTransformOptions = {};

  if (hasSize || hasFx || options.format !== undefined || options.quality !== undefined) {
    // Resized / filtered variants always go out as webp unless caller forces jpeg/png
    normalized.format = options.format || DEFAULT_FORMAT;
    normalized.quality = options.quality ?? DEFAULT_QUALITY;
    if (hasSize) {
      normalized.fit = options.fit || DEFAULT_FIT;
    } else if (options.fit) {
      normalized.fit = options.fit;
    }
  }

  if (options.width !== undefined) {
    normalized.width = snapToAllowed(options.width, ALLOWED_WIDTHS);
  }
  if (options.height !== undefined) {
    normalized.height = snapToAllowed(options.height, ALLOWED_HEIGHTS);
  }
  if (normalized.quality !== undefined) {
    normalized.quality = Math.max(1, Math.min(100, Math.round(normalized.quality)));
  }
  if (normalized.format === 'jpg') {
    normalized.format = 'jpeg';
  }
  if (options.rotate !== undefined && !Number.isNaN(options.rotate)) {
    normalized.rotate = ((Math.round(options.rotate) % 360) + 360) % 360;
  }
  if (options.flip) normalized.flip = true;
  if (options.flop) normalized.flop = true;
  if (greyscale) normalized.greyscale = true;

  return normalized;
}

/** Nest /api/media/transform query string */
export function buildImageTransformQuery(params: ImageTransformOptions): string {
  const ordered = new URLSearchParams();
  if (params.width !== undefined) ordered.set('width', String(params.width));
  if (params.height !== undefined) ordered.set('height', String(params.height));
  if (params.fit) ordered.set('fit', params.fit);
  if (params.format) ordered.set('format', String(params.format));
  if (params.quality !== undefined) ordered.set('quality', String(params.quality));
  if (params.rotate !== undefined) ordered.set('rotate', String(params.rotate));
  if (params.flip) ordered.set('flip', 'true');
  if (params.flop) ordered.set('flop', 'true');
  if (params.greyscale) ordered.set('greyscale', 'true');
  return ordered.toString();
}

function fitPathSegment(fit: ImageTransformOptions['fit']): string {
  if (fit === 'inside' || fit === 'contain' || fit === 'outside') return 'inside';
  return 'cover';
}

/**
 * Public path prefix: {W}x{H}/{quality}/{fit}/
 * Örnek: 1260x700/85/cover/
 */
export function buildDitTransformPathPrefix(params: ImageTransformOptions): string {
  const w = params.width;
  const h = params.height;
  const quality = params.quality ?? DEFAULT_QUALITY;
  const fit = fitPathSegment(params.fit || DEFAULT_FIT);
  const sizePart =
    w !== undefined || h !== undefined ? `${w ?? 0}x${h ?? 0}/` : '';
  const bw = params.greyscale ? 'bw/' : '';
  return `${sizePart}${quality}/${fit}/${bw}`;
}

export interface BuildImageCdnUrlOptions {
  /** Nest transform base — only when no CDN domain */
  apiBaseUrl?: string;
}

function resolveCdnDomain(cdnDomain?: string): string | undefined {
  if (cdnDomain?.trim()) {
    return cdnDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }
  if (typeof process !== 'undefined' && process.env) {
    const fromEnv =
      process.env.NEXT_PUBLIC_IMAGE_CDN_DOMAIN ||
      process.env.IMAGE_CDN_DOMAIN ||
      process.env.NEXT_PUBLIC_IMAGE_CDN_URL;
    if (fromEnv?.trim()) {
      return fromEnv.replace(/^https?:\/\//, '').replace(/\/$/, '');
    }
  }
  return undefined;
}

function needsTransform(normalized: ImageTransformOptions): boolean {
  return (
    normalized.width !== undefined ||
    normalized.height !== undefined ||
    normalized.rotate !== undefined ||
    !!normalized.flip ||
    !!normalized.flop ||
    !!normalized.greyscale ||
    (normalized.format !== undefined &&
      !['jpeg', 'jpg', 'png'].includes(normalized.format)) ||
    (normalized.quality !== undefined && normalized.quality !== 100)
  );
}

/**
 * Prefer CDN with path style. Nest transform only without CDN.
 */
export function buildImageCdnUrl(
  path: string,
  options: ImageTransformOptions = {},
  cdnDomain?: string,
  _paramStyle?: ParamStyle,
  opts?: BuildImageCdnUrlOptions
): string {
  const domain = resolveCdnDomain(cdnDomain);
  const cleanPath = path.trim().replace(/^\/+/, '');
  const normalizedPath = cleanPath ? `/${cleanPath}` : '/';
  const normalized = normalizeOptions(options);
  const transform = needsTransform(normalized);

  if (domain) {
    if (!transform) {
      return `https://${domain}${normalizedPath}`;
    }

    const prefix = buildDitTransformPathPrefix(normalized);
    // S3 key must stay as stored (do not rewrite extension)
    const urlPath = `/${prefix}${cleanPath}`.replace(/\/{2,}/g, '/');
    return `https://${domain}${urlPath}`;
  }

  if (transform) {
    const apiBase = opts?.apiBaseUrl || API_BASE_URL || '';
    if (apiBase) {
      const base = apiBase.replace(/\/$/, '');
      const query = buildImageTransformQuery(normalized);
      return `${base}/api/media/transform?path=${encodeURIComponent(cleanPath)}&${query}`;
    }
  }

  const apiBase = opts?.apiBaseUrl || API_BASE_URL || '';
  if (apiBase) {
    return `${apiBase.replace(/\/$/, '')}/storage${normalizedPath}`;
  }

  return normalizedPath;
}
